import React, { useState, useEffect, useMemo } from 'react';
import { useDispatch, useSelector } from "react-redux";
import { getLabRatings, updateRatingStatus } from '../../apiCalls/ratings';
import moment from "moment/moment";

const StarDisplay = ({ rating }) => {
  const filled = Math.round(Number(rating) || 0);
  const colors = ['#fbbf24', '#f59e0b', '#d97706'];
  const color = filled >= 4 ? colors[0] : filled >= 3 ? colors[1] : colors[2];
  return (
    <div style={{ display: 'inline-flex', alignItems: 'center', gap: '2px' }}>
      {Array.from({ length: 5 }, (_, i) => (
        <i
          key={i}
          className={`bi bi-star${i < filled ? '-fill' : ''}`}
          style={{ fontSize: '0.8rem', color: i < filled ? color : '#d1d5db' }}
        />
      ))}
      <span style={{
        marginLeft: '6px',
        fontSize: '0.82rem',
        fontWeight: 700,
        color: color,
        fontFamily: 'var(--font-sans)'
      }}>
        {Number(rating).toFixed(1)}
      </span>
    </div>
  );
};

const RatingBadge = ({ rating }) => {
  const n = Number(rating) || 0;
  let bg, color, label;
  if (n >= 4) { bg = '#d1fae5'; color = '#065f46'; label = 'Excellent'; }
  else if (n >= 3) { bg = '#fef3c7'; color = '#92400e'; label = 'Good'; }
  else if (n >= 2) { bg = '#fee2e2'; color = '#991b1b'; label = 'Average'; }
  else { bg = '#f3f4f6'; color = '#6b7280'; label = 'Poor'; }
  return (
    <span style={{
      display: 'inline-flex', alignItems: 'center',
      padding: '0.2rem 0.6rem', borderRadius: '9999px',
      fontSize: '0.68rem', fontWeight: 700,
      background: bg, color,
      letterSpacing: '0.3px'
    }}>
      {label}
    </span>
  );
};

const Ratings = () => {
  const dispatch = useDispatch();
  const users    = useSelector((state) => state.user);
  const ratings  = useSelector((state) => state.rating.ratings);
  const { _id }  = users?.currentUser;

  const [checked,    setChecked]    = useState(false);
  const [search,     setSearch]     = useState("");
  const [starFilter, setStarFilter] = useState("");

  useEffect(() => {
    if (_id) getLabRatings(dispatch, _id);
  }, [_id, dispatch, checked]);

  const handleToggleStatus = async (id) => {
    const res = await updateRatingStatus(id);
    if (res?.data?.success) setChecked((c) => !c);
  };

  const displayed = useMemo(() => {
    let list = ratings ? [...ratings] : [];
    if (search.trim()) {
      const q = search.toLowerCase();
      list = list.filter(
        (r) =>
          r.fullname?.toLowerCase().includes(q) ||
          r.reviewMessage?.toLowerCase().includes(q)
      );
    }
    if (starFilter) {
      list = list.filter((r) => Math.round(r.rating) === Number(starFilter));
    }
    return list;
  }, [ratings, search, starFilter]);

  const hasFilters = search || starFilter;
  const resetFilters = () => { setSearch(""); setStarFilter(""); };

  // summary stats
  const total    = ratings?.length || 0;
  const avgRating = total ? (ratings.reduce((s, r) => s + Number(r.rating || 0), 0) / total).toFixed(1) : "0.0";
  const visible  = ratings?.filter((r) => r.status).length || 0;

  return (
    <div className="dt-page">

      {/* ── Header ── */}
      <div className="dt-header">
        <div>
          <h4 className="dt-title">Reviews &amp; Ratings</h4>
          <p className="dt-subtitle" style={{ color: 'var(--gray-400)', fontSize: '0.8rem' }}>
            {displayed.length} review{displayed.length !== 1 ? 's' : ''} shown
          </p>
        </div>
      </div>

      {/* ── Summary cards ── */}
      <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fit, minmax(160px, 1fr))', gap: '1rem' }}>
        {[
          { label: 'Total Reviews', value: total, icon: 'bi-chat-square-text', color: '#8166f4', bg: '#f3f0ff' },
          { label: 'Avg Rating',    value: avgRating, icon: 'bi-star-half',      color: '#f59e0b', bg: '#fef3c7' },
          { label: 'Visible',       value: visible,   icon: 'bi-eye',            color: '#10b981', bg: '#d1fae5' },
          { label: 'Hidden',        value: total - visible, icon: 'bi-eye-slash', color: '#ef4444', bg: '#fee2e2' },
        ].map((s) => (
          <div key={s.label} style={{
            background: '#fff',
            border: '1px solid var(--gray-100)',
            borderRadius: 'var(--radius-lg)',
            padding: '1rem 1.25rem',
            display: 'flex', alignItems: 'center', gap: '0.875rem',
            boxShadow: '0 2px 12px rgba(129,102,244,0.06)'
          }}>
            <div style={{
              width: 44, height: 44, borderRadius: 'var(--radius-md)',
              background: s.bg, display: 'flex', alignItems: 'center',
              justifyContent: 'center', flexShrink: 0
            }}>
              <i className={`bi ${s.icon}`} style={{ fontSize: '1.15rem', color: s.color }} />
            </div>
            <div>
              <div style={{ fontSize: '1.35rem', fontWeight: 700, color: 'var(--gray-900)', lineHeight: 1 }}>
                {s.value}
              </div>
              <div style={{ fontSize: '0.75rem', color: 'var(--gray-500)', marginTop: 3 }}>
                {s.label}
              </div>
            </div>
          </div>
        ))}
      </div>

      {/* ── Toolbar ── */}
      <div className="dt-toolbar">
        <div className="dt-search-wrap">
          <i className="bi bi-search dt-search-icon" />
          <input
            type="text"
            className="dt-search"
            placeholder="Search by name or message…"
            value={search}
            onChange={(e) => setSearch(e.target.value)}
          />
          {search && (
            <button className="dt-search-clear" onClick={() => setSearch("")}>
              <i className="bi bi-x" />
            </button>
          )}
        </div>

        <div className="dt-filter-group">
          <label className="dt-filter-label">
            <i className="bi bi-star" /> Filter by stars
          </label>
          <select
            className="dt-select"
            value={starFilter}
            onChange={(e) => setStarFilter(e.target.value)}
          >
            <option value="">All ratings</option>
            {[5, 4, 3, 2, 1].map((n) => (
              <option key={n} value={n}>
                {'★'.repeat(n)} {n} star{n !== 1 ? 's' : ''}
              </option>
            ))}
          </select>
        </div>

        {hasFilters && (
          <button className="dt-reset-btn" onClick={resetFilters}>
            <i className="bi bi-arrow-counterclockwise" /> Reset
          </button>
        )}
      </div>

      {/* ── Table ── */}
      <div className="dt-wrap">
        {displayed.length === 0 ? (
          <div className="dt-empty">
            <i className="bi bi-star dt-empty-icon" />
            <p style={{ color: 'var(--gray-400)', fontSize: '0.9rem' }}>
              {hasFilters ? 'No reviews match your filters' : 'No reviews yet'}
            </p>
            {hasFilters && (
              <button className="dt-reset-btn" onClick={resetFilters}>
                Clear filters
              </button>
            )}
          </div>
        ) : (
          <table className="dt-table">
            <thead>
              <tr>
                <th style={{ width: '3rem' }}>#</th>
                <th>Reviewer</th>
                <th>Rating</th>
                <th>Quality</th>
                <th>Review</th>
                <th>Date</th>
                <th>Visibility</th>
              </tr>
            </thead>
            <tbody>
              {displayed.map((data, index) => (
                <tr key={data._id}>

                  {/* # */}
                  <td className="dt-cell-num">{index + 1}</td>

                  {/* Reviewer */}
                  <td>
                    <div className="dt-person">
                      <div className="dt-avatar">
                        {(data?.fullname || '?')[0].toUpperCase()}
                      </div>
                      <div>
                        <div className="dt-person-name">{data?.fullname}</div>
                      </div>
                    </div>
                  </td>

                  {/* Stars */}
                  <td>
                    <StarDisplay rating={data?.rating} />
                  </td>

                  {/* Quality badge */}
                  <td>
                    <RatingBadge rating={data?.rating} />
                  </td>

                  {/* Message */}
                  <td>
                    {data?.reviewMessage ? (
                      <span
                        title={data.reviewMessage}
                        style={{
                          display: 'block',
                          maxWidth: '280px',
                          whiteSpace: 'nowrap',
                          overflow: 'hidden',
                          textOverflow: 'ellipsis',
                          color: 'var(--gray-600)',
                          fontSize: '0.84rem'
                        }}
                      >
                        {data.reviewMessage}
                      </span>
                    ) : (
                      <span style={{ color: 'var(--gray-300)', fontSize: '0.82rem' }}>—</span>
                    )}
                  </td>

                  {/* Date */}
                  <td>
                    <div style={{ display: 'flex', flexDirection: 'column', gap: '2px' }}>
                      <span style={{ fontWeight: 600, color: 'var(--gray-800)', fontSize: '0.84rem' }}>
                        {moment(data.createdAt).format('DD MMM YYYY')}
                      </span>
                      <span style={{ fontSize: '0.72rem', color: 'var(--gray-400)' }}>
                        {moment(data.createdAt).format('hh:mm A')}
                      </span>
                    </div>
                  </td>

                  {/* Toggle visibility */}
                  <td>
                    <button
                      onClick={() => handleToggleStatus(data?._id)}
                      title={data?.status ? 'Click to hide' : 'Click to show'}
                      style={{
                        display: 'inline-flex',
                        alignItems: 'center',
                        gap: '0.35rem',
                        padding: '0.32rem 0.75rem',
                        borderRadius: 'var(--radius-md)',
                        fontSize: '0.78rem',
                        fontWeight: 600,
                        cursor: 'pointer',
                        fontFamily: 'var(--font-sans)',
                        border: '1px solid',
                        transition: 'all 0.15s',
                        whiteSpace: 'nowrap',
                        background: data?.status ? 'var(--success-light)' : 'var(--danger-light)',
                        color: data?.status ? '#065f46' : '#dc2626',
                        borderColor: data?.status ? '#a7f3d0' : '#fca5a5',
                      }}
                    >
                      <i className={`bi bi-eye${data?.status ? '' : '-slash'}`} />
                      {data?.status ? 'Visible' : 'Hidden'}
                    </button>
                  </td>

                </tr>
              ))}
            </tbody>
          </table>
        )}
      </div>
    </div>
  );
};

export default Ratings;
